import Microgame from '../engine/Microgame.js';

export default class SprayGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Vaporise !`, en: `Spray!`, es: `¡Rocía!` },
            controls: { fr: `Maintiens appuyé et bouge pour recouvrir la zone`, en: `Hold and move to cover the area`, es: `Mantén presionado y muévete para cubrir el área` },
            objective: { fr: `Recouvre entièrement l'objectif avec de la peinture`, en: `Cover the entire target with paint`, es: `Cubre todo el objetivo con pintura` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M12 2A10 10 0 1 0 12 22 10 10 0 0 0 12 2Zm0 0v20"/><circle cx="12" cy="12" r="10"/></svg>`,
            actions: [
                { type: 'wait', duration: 220 },
                { type: 'hold', x: 40, y: 34, holdDuration: 920 },
                { type: 'wait', duration: 120 },
                { type: 'hold', x: 50, y: 52, holdDuration: 920 },
                { type: 'wait', duration: 120 },
                { type: 'hold', x: 62, y: 38, holdDuration: 920 },
                { type: 'wait', duration: 420 },
            ],
            loopDuration: 4200
        };
    }
    constructor(engine) {
        super(engine);
        this.fires = [];
        this.particles = [];
        this.isSpraying = false;
        this.targetX = 0;
        this.targetY = 0;
        
        this._isVictoryAnimating = false;
        this._frozenTimerValue = 0;

        const speedMult = engine.speedMultiplier || 1.0;
        // 4.5s at x1, ~2.15s at x4 (added 100ms progressively)
        const adjustedSpeed = 1 + (speedMult - 1) * 0.36;
        this.duration = 4500 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "EXTINGUISH!";

        // Building Structure (Background)
        const building = document.createElement('div');
        building.style.position = 'absolute';
        building.style.width = '240px';
        building.style.height = '300px';
        building.style.background = 'rgba(255, 255, 255, 0.05)';
        building.style.border = `2px solid rgba(255, 255, 255, 0.1)`;
        building.style.top = '45%';
        building.style.left = '50%';
        building.style.transform = 'translate(-50%, -50%)';
        building.style.borderRadius = '10px';
        building.style.zIndex = '1';
        this.container.appendChild(building);

        // Windows for decoration
        for (let i = 0; i < 6; i++) {
            const win = document.createElement('div');
            win.style.position = 'absolute';
            win.style.width = '40px';
            win.style.height = '40px';
            win.style.background = 'rgba(255, 255, 255, 0.05)';
            win.style.top = `${20 + Math.floor(i/2) * 80}px`;
            win.style.left = `${40 + (i%2) * 120}px`;
            win.style.borderRadius = '5px';
            building.appendChild(win);
        }

        // 3 Fires (Secondary Color)
        for (let i = 0; i < 3; i++) {
            const fire = document.createElement('div');
            fire.style.position = 'absolute';
            fire.style.width = '60px';
            fire.style.height = '60px';
            fire.style.background = this.secondaryColor;
            fire.style.borderRadius = '50% 50% 50% 10%';
            fire.style.transform = 'rotate(-45deg)';
            fire.style.boxShadow = `0 0 20px ${this.secondaryColor}`;
            fire.style.zIndex = '5';
            
            // Random position on building
            const fixedFires = [
                { x: 40, y: 50 },
                { x: 120, y: 160 },
                { x: 180, y: 70 }
            ];
            const fx = this.isPlayback ? fixedFires[i].x : (40 + Math.random() * 160);
            const fy = this.isPlayback ? fixedFires[i].y : (40 + Math.random() * 220);
            fire.style.left = `${fx}px`;
            fire.style.top = `${fy}px`;
            
            fire.dataset.health = 100;
            building.appendChild(fire);
            this.fires.push(fire);

            // Flicker animation
            fire.animate([
                { transform: 'rotate(-45deg) scale(0.9)', filter: 'brightness(1)' },
                { transform: 'rotate(-45deg) scale(1.1)', filter: 'brightness(1.5)' }
            ], {
                duration: 200 + Math.random() * 200,
                iterations: Infinity,
                direction: 'alternate'
            });
        }

        // Spray Can (Primary Color)
        this.can = document.createElement('div');
        this.can.style.position = 'absolute';
        this.can.style.width = '50px';
        this.can.style.height = '100px';
        this.can.style.background = this.primaryColor;
        this.can.style.bottom = '5%';
        this.can.style.left = '50%';
        this.can.style.transform = 'translateX(-50%)';
        this.can.style.borderRadius = '8px';
        this.can.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.can.style.zIndex = '20';
        this.container.appendChild(this.can);

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    update(dt) {
        if (this._isVictoryAnimating) {
            this.engine.timer = this._frozenTimerValue;
            return;
        }
        if (!this.active || this.won) return;

        // Particle System Update
        if (this.isSpraying) {
            this.spawnParticles();
        }

        this.particles.forEach((p, index) => {
            p.y += p.vy;
            p.x += p.vx;
            p.life -= 0.03;
            p.el.style.left = `${p.x}px`;
            p.el.style.top = `${p.y}px`;
            p.el.style.opacity = p.life;
            p.el.style.transform = `scale(${p.life * 2})`;

            if (p.life <= 0) {
                p.el.remove();
                this.particles.splice(index, 1);
            } else {
                this.checkParticleHit(p);
            }
        });

        // Win condition check
        if (this.fires.every(f => parseFloat(f.dataset.health) <= 0)) {
            this.handleWin();
        }
    }

    spawnParticles() {
        // Emit from top of the can
        const rect = this.can.getBoundingClientRect();
        const cRect = this.container.getBoundingClientRect();
        
        for (let i = 0; i < 2; i++) {
            const el = document.createElement('div');
            el.style.position = 'absolute';
            el.style.width = '8px';
            el.style.height = '8px';
            el.style.background = this.primaryColor;
            el.style.borderRadius = '50%';
            el.style.pointerEvents = 'none';
            el.style.boxShadow = `0 0 5px ${this.primaryColor}`;
            el.style.zIndex = '10';
            this.container.appendChild(el);

            const startX = rect.left + rect.width / 2 - cRect.left;
            const startY = rect.top - cRect.top;

            // Aim towards target
            const angle = Math.atan2(this.targetY - startY, this.targetX - startX);
            const speed = 12 + Math.random() * 4;
            
            this.particles.push({
                el,
                x: startX,
                y: startY,
                vx: Math.cos(angle) * speed + (Math.random() - 0.5) * 2,
                vy: Math.sin(angle) * speed + (Math.random() - 0.5) * 2,
                life: 1.0
            });
        }
    }

    checkParticleHit(p) {
        this.fires.forEach(fire => {
            const hp = parseFloat(fire.dataset.health);
            if (hp <= 0) return;

            const rect = fire.getBoundingClientRect();
            const cRect = this.container.getBoundingClientRect();
            const fx = rect.left + rect.width/2 - cRect.left;
            const fy = rect.top + rect.height/2 - cRect.top;

            const dist = Math.hypot(p.x - fx, p.y - fy);
            if (dist < 30) {
                const newHp = Math.max(0, hp - 2.5);
                fire.dataset.health = newHp;
                fire.style.transform = `rotate(-45deg) scale(${newHp / 100})`;
                fire.style.opacity = newHp / 100;
                
                // Visual spark on hit
                p.life = 0; 
            }
        });
    }

    onDown(e) {
        if (!this.active || this.won) return;
        this.isSpraying = true;
        this.updateTarget(e);
    }

    onMove(e) {
        if (!this.active || this.won) return;
        this.updateTarget(e);
        
        // Move can horizontally
        const cRect = this.container.getBoundingClientRect();
        const x = e.clientX - cRect.left;
        this.can.style.left = `${x}px`;
    }

    updateTarget(e) {
        const cRect = this.container.getBoundingClientRect();
        this.targetX = e.clientX - cRect.left;
        this.targetY = e.clientY - cRect.top;
    }

    onUp() {
        this.isSpraying = false;
    }

    handleWin() {
        if (this.won) return;
        this.won = true;
        this._isVictoryAnimating = true;
        this._frozenTimerValue = this.engine.timer;
        
        // Final polish animation
        this.isSpraying = false;
        this.can.animate([
            { transform: 'translateX(-50%) scale(1)' },
            { transform: 'translateX(-50%) scale(1.2)' },
            { transform: 'translateX(-50%) scale(1)' }
        ], { duration: 500 });

        setTimeout(() => {
            this._isVictoryAnimating = false;
            this.win();
        }, 800);
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
        this.particles.forEach(p => p.el.remove());
    }
}

